<template>
  <auth-layout
    title="Welcome back"
    subtitle="Sign in to your backoffice account to continue."
  >
    <form
      class="c-login"
      novalidate
      @submit.prevent="validateUser"
    >
      <div
        v-if="loginError"
        class="ui-alert ui-alert--danger"
        role="alert"
      >
        <app-icon name="error" />
        <span>{{ loginError }}</span>
      </div>

      <div :class="['ui-field', { 'ui-field--invalid': $v.userData.email.$error }]">
        <label
          class="ui-field__label"
          for="login-email"
        >Email</label>
        <div class="ui-field__control">
          <app-icon
            class="ui-field__icon"
            name="mail"
          />
          <input
            id="login-email"
            v-model.trim="userData.email"
            v-focus
            class="ui-field__input"
            type="email"
            name="email"
            placeholder="you@company.com"
            autocomplete="username"
            :aria-invalid="$v.userData.email.$error"
          >
        </div>
        <span
          v-if="$v.userData.email.$error"
          class="ui-field__error"
        >
          {{ !$v.userData.email.required ? 'Email is required' : 'Enter a valid email address' }}
        </span>
      </div>

      <div :class="['ui-field', { 'ui-field--invalid': $v.userData.password.$error }]">
        <label
          class="ui-field__label"
          for="login-password"
        >Password</label>
        <div class="ui-field__control ui-field__control--with-action">
          <app-icon
            class="ui-field__icon"
            name="lock"
          />
          <input
            id="login-password"
            v-model="userData.password"
            class="ui-field__input"
            :type="showPassword ? 'text' : 'password'"
            name="password"
            placeholder="Enter your password"
            autocomplete="current-password"
            :aria-invalid="$v.userData.password.$error"
          >
          <button
            type="button"
            class="ui-field__action"
            :aria-label="showPassword ? 'Hide password' : 'Show password'"
            :title="showPassword ? 'Hide password' : 'Show password'"
            @click="showPassword = !showPassword"
          >
            <app-icon :name="showPassword ? 'visibility_off' : 'visibility'" />
          </button>
        </div>
        <span
          v-if="$v.userData.password.$error"
          class="ui-field__error"
        >Password is required</span>
      </div>

      <button
        type="submit"
        :class="['ui-btn ui-btn--primary ui-btn--lg ui-btn--block c-login__submit', { 'ui-btn--loading': isSubmitting }]"
        :disabled="isSubmitting"
      >
        Sign in
        <app-icon name="arrow_forward" />
      </button>
    </form>
  </auth-layout>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import AuthLayout from "../../shared/auth-layout/AuthLayout";
import AppIcon from "../../shared/app-icon/AppIcon";
import { validationBaseMixin } from '../../mixins/ValidationBaseMixin'
import { email, required, } from 'vuelidate/lib/validators'
import router from '../../router/router'
import {onlinePermissionsMixin} from "@/mixins/onlinePermissionsMixin";
import { authService } from '@/services/auth';

export default {
  components: {
    AuthLayout,
    AppIcon,
  },
  mixins: [validationBaseMixin, onlinePermissionsMixin],
  data: () => {
    return {
      userData: {
        email: null,
        password: null,
      },
      showPassword: false,
      isSubmitting: false,
    }
  },
  validations: {
    userData: {
      password: {
        required
      },
      email: {
        required,
        email
      }
    }
  },
  computed: {
    ...mapState('account', ['loginError', 'needsActivation'])
  },
  methods: {
    ...mapActions('account', ['handleLogin']),

    validateUser () {
      this.$v.$touch();

      if (!this.$v.$invalid && !this.isSubmitting) {
        this.login()
      }
    },

    async login () {
      this.isSubmitting = true

      try {
        const response = await this.handleLogin({username: this.userData.email, password: this.userData.password})

        // account needs activation: there is no session or permissions yet, so stop here
        if (!response.data) {
          await router.push({name: 'activate', query: {email: this.userData.email}});
          return;
        }

        if (this.isAdmin()) {
          router.push({ name: "websites" });
        } else {
          let firstAllowedRoutePathName = this.getFirstAllowedRoutePathName();

          // the login response can come without the user's online permissions (they are complete
          // in check-auth, which is why a reload worked): load them before picking the first page
          if (!firstAllowedRoutePathName) {
            const checkAuthData = await authService.checkLogin();

            if (checkAuthData) {
              authService.preparePermissionsAndRoutes(checkAuthData);
            }

            firstAllowedRoutePathName = this.getFirstAllowedRoutePathName();
          }

          // no page this user may open: say so instead of silently staying on the login form
          if (!firstAllowedRoutePathName) {
            this.$store.commit('account/LOGIN_ERROR', 'Your account has no access to any section yet. Ask an administrator to assign permissions.');
            return;
          }

          router.push({ name: firstAllowedRoutePathName });
        }

      } catch (error) {
        throw error
      } finally {
        this.isSubmitting = false
      }
    }

  }
}
</script>

<style lang="scss" scoped>
  .c-login {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .c-login__submit {
    margin-top: 8px;
  }
</style>
